import { defineComponent as _defineComponent } from 'vue'
import {ref, onMounted, computed, watch } from 'vue';
import { CaffeinatedProductType } from '@/models/Cautionary/CaffeinatedProductType';
import { CautionaryFoodType } from '@/models/Cautionary/CautionaryFoodType';
import { EnumItem } from '@/models/Cautionary/CautionaryProductTypeOptions';
import { CautionarySideOfPackRenderData } from '@/models/NutritionLabels/ExportLabelState';
import { useTranslations } from '@/hooks/useTranslations';
import CautionaryRepository from '@/utils/API/CautionaryRepository';
import { LabelLanguage } from '@/models/NutritionLabels/LabelLanguage';
import VBanner from "@/components/vBanner.vue";
import { interceptEnterKey } from "@/utils/Helpers";


export default /*#__PURE__*/_defineComponent({
  __name: 'vCautionaryInputs',
  props: {
    value: null,
    labelLanguage: null,
    recipeId: null,
    showWidthAndMessage: { type: Boolean },
    disableInputs: { type: Boolean }
  },
  emits: [
  'cautionary-qualification',
  'update:value',
  'preview-update',
],
  setup(__props: any, { emit }) {

const props = __props as {
  value: CautionarySideOfPackRenderData,
  labelLanguage: LabelLanguage
  recipeId: number;
  showWidthAndMessage: boolean;
  disableInputs: boolean;
};



const componentModel = ref<CautionarySideOfPackRenderData>(structuredClone(props.value));

watch(componentModel, (newVal) => {
  emit('update:value', newVal);
}, { deep: true });



const isCaffeinatedFoodTypeSelected = ref(false);
const isChewingGumSelected = ref(false);
const foodTypeOptions = ref<EnumItem[]>([]);
const caffeinatedFoodOptions = ref<EnumItem[]>([]);

let translations = ref(useTranslations());

onMounted(() => {
  fetchCautionaryFoodOptions();
});

const warningMessageKey = computed(() => {
  if (!isCaffeinatedFoodTypeSelected.value) {
    return '';
  }

  switch (componentModel.value.caffeinatedType) {
  case CaffeinatedProductType.CarbonatedBeverages:
  case CaffeinatedProductType.NonCarbonatedBeverages:
    return 'nl_c_fop_carbonated_beverage_conditions';
  case CaffeinatedProductType.CaffeinatedEnergyDrink:
    return 'nl_c_fop_caffeinated_energy_drink_conditions';
  case CaffeinatedProductType.ProteinIsolateAndCerealBars:
    return 'nl_c_fop_protein_isolate_cereal_bars_conditions';
  case CaffeinatedProductType.ChocolateConfectionaries:
    return 'nl_c_fop_chocolate_confectionaries_conditions';
  case CaffeinatedProductType.ChewingGum:
    return 'nl_c_fop_chewing_gum_conditions';
  default:
    return '';
  }
});

const selectedFoodType = computed(() => foodTypeOptions.value.find((o) => o.value === props.value.foodType));
const selectedCaffeinatedType = computed(() => caffeinatedFoodOptions.value.find((o) => o.value === props.value.caffeinatedType));

async function fetchCautionaryFoodOptions() {
  try {
    const response = await CautionaryRepository.getCautionaryProductTypes();
    foodTypeOptions.value = response.data.cautionaryFoodTypes.filter(
      (item: EnumItem) => item.value !== CautionaryFoodType.None
    );

    caffeinatedFoodOptions.value = response.data.caffeinatedProductTypes.filter(
      (item: EnumItem) => item.value !== CaffeinatedProductType.None
    );
  }

  catch (error) {
    console.error('Error loading food options:', error);
  }
}

async function updateCautionaryLabelImage(): Promise<string> {
  try {
    // Default LabelLanguage to English (1) if undefined or invalid
    const labelLanguage = props.labelLanguage;
    const validLabelLanguage = Object.values(LabelLanguage).includes(labelLanguage)
      ? labelLanguage
      : LabelLanguage.English;

    const response = await CautionaryRepository.getCautionaryLabelImage(
      props.recipeId,
      componentModel.value.labelWidth,
      validLabelLanguage,
      componentModel.value.caffeinatedType,
      componentModel.value.foodType,
      componentModel.value.quantityInPackage,
    );

    const previewUrl = URL.createObjectURL(response.data);
    emit('preview-update', previewUrl);

    // Emit both the success state and the preview URL
    emit("cautionary-qualification", true, previewUrl);
  }

  catch (error) {
    emit("cautionary-qualification", false, null);
    console.error('Error fetching cautionary label image:', error);
  }
}

async function handleFoodTypeChange(selected: EnumItem) {
  isCaffeinatedFoodTypeSelected.value = selected.value === CautionaryFoodType.CaffeinatedSupplementedFood;
  componentModel.value.foodType = selected.value;

  if (selected.value === CautionaryFoodType.NonCaffeinatedSupplementedFood) {
    await resetState();
    await updateCautionaryLabelImage();
  }
}

async function handleCaffeinatedTypeChange(selected: EnumItem) {
  isChewingGumSelected.value = selected.value === CaffeinatedProductType.ChewingGum;
  componentModel.value.caffeinatedType = selected.value;

  await updateCautionaryLabelImage();
}

async function handleChewingGumChange($event: InputEvent) {
  const eventTarget = $event.target as HTMLInputElement;
  componentModel.value.quantityInPackage = parseInt(eventTarget.value);

  if (eventTarget.value > 0) {
    await updateCautionaryLabelImage();
  }
}

async function handleLabelWidthChange($event: InputEvent) {
  const eventTarget = $event.target as HTMLInputElement;
  componentModel.value.labelWidth = parseInt(eventTarget.value);
  await updateCautionaryLabelImage();
}

async function resetState() {
  isCaffeinatedFoodTypeSelected.value = false;
  isChewingGumSelected.value = false;

  componentModel.value.caffeinatedType = 0;
  componentModel.value.quantityInPackage = 0;
}

function translate(key: string) {
  return translations.value.get(key);
}

return { __sfc: true,props, componentModel, emit, isCaffeinatedFoodTypeSelected, isChewingGumSelected, foodTypeOptions, caffeinatedFoodOptions, translations, warningMessageKey, selectedFoodType, selectedCaffeinatedType, fetchCautionaryFoodOptions, updateCautionaryLabelImage, handleFoodTypeChange, handleCaffeinatedTypeChange, handleChewingGumChange, handleLabelWidthChange, resetState, translate, VBanner, interceptEnterKey }
}

})